/**
 * Creates a minimalist factory for Plotly chart options without deep merging.
 * All global defaults are pre-configured internally.
 * It exposes only one method: `createChartOptions` for generating chart configs.
 *
 * This version prioritizes performance by using shallow copies and
 * explicit property assignment over deep recursion for merging.
 * @returns {object} An object containing the `createChartOptions` method.
 */
function createPlotlyOptionsFactory() {
  const baseLayoutDefaults = {
    font: {
      family: 'Geist, sans-serif',
      size: 13,
      color: 'rgba(107, 114, 128, 1)',
    },
    hovermode: 'closest',
    autosize: true,
    margin: {
      l: 10, r: 10, b: 10, t: 10,
    },
    paper_bgcolor: 'white',
    plot_bgcolor: 'white',
    showlegend: false,
    title: {
      text: '',
      font: { size: 18, color: '#212529' },
      x: 0.5, y: 0.95, xanchor: 'center', yanchor: 'top', pad: { b: 15 }
    },
    xaxis: {
      title: { text: '', font: { size: 14, color: 'rgba(107, 114, 128, 1)' } },
      automargin: true, showgrid: false, zeroline: true,
      tickfont: { family: 'Geist, sans-serif', size: 12, color: '#6C757D' },
      tickmode: 'auto', nticks: null, range: null,
      showline: false, linewidth: 1, linecolor: '#CED4DA',
      ticks: 'outside', ticklen: 6, tickwidth: 1, tickcolor: '#CED4DA',
      hoverformat: '.0f',
    },
    yaxis: {
      title: { text: '', font: { size: 14, color: 'rgba(107, 114, 128, 1)' } },
      automargin: true, showgrid: true, griddash: 'dot', zeroline: true,
      gridcolor: '#E9ECEF', zerolinecolor: '#CED4DA',
      tickfont: { family: 'Geist, sans-serif', size: 12, color: '#6C757D' },
      tickmode: 'auto', nticks: null, range: null,
      showline: false, linewidth: 1, linecolor: '#CED4DA',
      ticks: 'outside', ticklen: 5, tickwidth: 1, tickcolor: '#6C757D',
      hoverformat: '.2f',
    },
    legend: {
      x: 0.5, y: -0.1, xanchor: 'center', yanchor: 'top',
      orientation: 'h',
      bgcolor: 'rgba(255, 255, 255, 0.9)', bordercolor: 'rgba(0, 0, 0, 0.05)', borderwidth: 1,
      font: { size: 11, color: '#6C757D' },
      traceorder: 'normal',
    },
    uniformtext: { mode: 'hide', minsize: 9 },
    annotations: [],
    shapes: [],
    images: [],
    sliders: [],
    updatemenus: [],
  };

  const baseConfigDefaults = {
    responsive: true,
    displayModeBar: false,
    displaylogo: false,
    modeBarButtonsToRemove: [
      'zoom2d', 'pan2d', 'select2d', 'lasso2d', 'zoomIn2d', 'zoomOut2d',
      'autoScale2d', 'resetScale2d', 'hoverClosestGl2d', 'hoverCompareGl2d',
      'toggleHover', 'toggleSpikelines', 'sendDataToCloud', 'resetViews', 'toImage'
    ],
    scrollZoom: false,
    staticPlot: false,
    editable: false,
    doubleClick: 'reset+autosize',
    showAxisDragHandles: false,
    showAxisRangeEntryBoxes: false,
    locale: 'uk',
  };

  const baseTraceDefaults = {
    bar: {
      type: 'bar',
      marker: {
        color: 'rgba(60, 179, 113, 0.9)',
        opacity: 0.9,
        line: { width: 0.5, color: 'rgba(60, 179, 113, 1)' }
      },
      bargap: 0.35,
      bargroupgap: 0,
      width: null,
      base: 0,
      offset: 0,
      hoverinfo: 'x+y',
    },
    scatter: {
      type: 'scatter',
      mode: 'lines+markers',
      line: {
        color: 'rgba(70, 130, 180, 1)',
        width: 2.5,
        shape: 'spline',
        dash: 'solid',
      },
      marker: {
        color: 'rgba(70, 130, 180, 1)',
        size: 7, symbol: 'circle',
        line: { width: 1, color: 'white' }
      },
      fill: 'none',
      hoverinfo: 'x+y+name',
    },
    pie: {
      type: 'pie',
      values: [], labels: [],
      hoverinfo: 'label+percent+name',
      pull: 0,
      hole: 0.4,
      marker: {
        colors: ['#4285F4', '#EA4335', '#FBBC05', '#34A853', '#A0A0A0', '#4A90E2', '#7ED321', '#F5A623', '#BD10E0'],
        line: { width: 1.5, color: 'white' }
      },
      textinfo: 'percent+label',
      textposition: 'inside',
      direction: 'clockwise',
      sort: true,
      opacity: 0.9,
    },
  };

  return {
    /**
     * Creates a complete Plotly chart options object (data, layout, config)
     * by merging internally defined global defaults with provided overrides.
     * This function uses shallow merging with spread syntax for performance.
     * @param {object} options - A single object containing properties to override the global defaults:
     * - `data`: (Array<object>, optional) An array of trace objects. Each trace will be merged with its type's default. Defaults to an empty array.
     * - `layout`: (object, optional) Layout properties to override.
     * - `config`: (object, optional) Configuration properties to override.
     * @returns {object} An object `{ data, layout, config }` ready for Plotly.newPlot or Plotly.react.
     */
    createChartOptions(options = {}) {
      const dataOverrides = options.data || [];
      const layoutOverrides = options.layout || {};
      const configOverrides = options.config || {};

      const mergedLayout = {
        ...baseLayoutDefaults,
        ...layoutOverrides,
        font: {
          ...baseLayoutDefaults.font,
          ...(layoutOverrides.font || {}),
        },
        margin: {
          ...baseLayoutDefaults.margin,
          ...(layoutOverrides.margin || {}),
        },
        xaxis: {
          ...baseLayoutDefaults.xaxis,
          ...(layoutOverrides.xaxis || {}),
          title: {
            ...baseLayoutDefaults.xaxis.title,
            ...((layoutOverrides.xaxis && layoutOverrides.xaxis.title) || {}),
          },
          tickfont: {
            ...baseLayoutDefaults.xaxis.tickfont,
            ...((layoutOverrides.xaxis && layoutOverrides.xaxis.tickfont) || {}),
          },
        },
        yaxis: {
          ...baseLayoutDefaults.yaxis,
          ...(layoutOverrides.yaxis || {}),
          title: {
            ...baseLayoutDefaults.yaxis.title,
            ...((layoutOverrides.yaxis && layoutOverrides.yaxis.title) || {}),
          },
          tickfont: {
            ...baseLayoutDefaults.yaxis.tickfont,
            ...((layoutOverrides.yaxis && layoutOverrides.yaxis.tickfont) || {}),
          },
        },
        title: {
          ...baseLayoutDefaults.title,
          ...(layoutOverrides.title || {}),
          font: {
            ...baseLayoutDefaults.title.font,
            ...((layoutOverrides.title && layoutOverrides.title.font) || {}),
          },
        },
      };

      const mergedConfig = {
        ...baseConfigDefaults,
        ...configOverrides,
      };

      const mergedData = dataOverrides.map(traceOverride => {
        const traceType = traceOverride.type || 'scatter';
        const defaultForType = baseTraceDefaults[traceType] || { type: traceType };

        return {
          ...defaultForType,
          ...traceOverride,
          marker: {
            ...(defaultForType.marker || {}),
            ...(traceOverride.marker || {}),
            pattern: {
              ...((defaultForType.marker && defaultForType.marker.pattern) || {}),
              ...((traceOverride.marker && traceOverride.marker.pattern) || {}),
            }
          }
        };
      });

      return {
        data: mergedData,
        layout: mergedLayout,
        config: mergedConfig,
      };
    },
  };
}

export const chartOptionsFactory = createPlotlyOptionsFactory();
